/**
 * ---------------------------------------------------------------------------
 * Cart drawer
 * ---------------------------------------------------------------------------
 * A thin enhancement layer on top of the theme's own Cart class (theme.js).
 * That class already owns opening on add-to-cart, re-rendering the cart,
 * quantity/remove, Escape and click-outside — so this file deliberately does
 * NOT reimplement any of it. It only adds what the drop-down never needed:
 *
 *   1. the dimmed overlay + page scroll lock
 *   2. closing from controls that live *inside* the drawer (the theme's
 *      click-outside handler cannot see those)
 *   3. resolving the upsell strip: Search & Discovery pairings, then the
 *      customizer collection, then Shopify's recommendation engine
 *   4. one-click add from the recommendation strip
 *
 * Everything is delegated or re-run on `cart:rerendered`, because the theme
 * replaces the drawer's innerHTML wholesale on every cart change.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var drawer = document.getElementById('mini-cart');

  if (!drawer || !drawer.classList.contains('cart-drawer')) {
    return;
  }

  var root = document.documentElement;
  var overlay = document.querySelector('[data-cart-drawer-overlay]');
  var cartActionItem = document.querySelector('.header__action-item--cart');
  var toggle = document.querySelector('[data-action="toggle-mini-cart"]');
  var recsCache = {};

  /* -------------------------------------------------------------------------
   * Open / close
   * ---------------------------------------------------------------------- */

  function isOpen() {
    return drawer.getAttribute('aria-hidden') === 'false';
  }

  /**
   * Closing is delegated to the theme's own toggle so there is a single source
   * of truth for open state, focus trapping and aria attributes.
   */
  function close() {
    if (isOpen() && toggle) {
      toggle.click();
    }
  }

  function lockScroll() {
    var scrollbarWidth = window.innerWidth - root.clientWidth;
    root.style.setProperty('--cart-drawer-scrollbar-width', scrollbarWidth + 'px');
    root.classList.add('cart-drawer-open');
  }

  function unlockScroll() {
    root.classList.remove('cart-drawer-open');
    root.style.removeProperty('--cart-drawer-scrollbar-width');
  }

  // The theme flips aria-hidden; we mirror it onto <html> so the overlay and
  // scroll lock stay in sync no matter what triggered the change.
  new MutationObserver(function () {
    if (isOpen()) {
      lockScroll();
      loadRecommendations();
    } else {
      unlockScroll();
    }
  }).observe(drawer, { attributes: true, attributeFilter: ['aria-hidden'] });

  if (overlay) {
    overlay.addEventListener('click', close);
  }

  document.addEventListener('click', function (event) {
    var trigger = event.target.closest ? event.target.closest('[data-cart-drawer-close]') : null;

    if (trigger) {
      event.preventDefault();
      close();
    }
  });

  /* -------------------------------------------------------------------------
   * Recommendations
   * ---------------------------------------------------------------------- */

  function buildUrl(seed, intent, limit) {
    // limit is capped at 10 by Shopify. We always ask for the maximum because
    // the section filters out anything already in the cart before rendering.
    return (
      window.routes.productRecommendationsUrl +
      '?section_id=cart-drawer-recommendations' +
      '&product_id=' + encodeURIComponent(seed) +
      '&limit=' + Math.min(Math.max(parseInt(limit, 10) || 6, 1) + 4, 10) +
      '&intent=' + encodeURIComponent(intent)
    );
  }

  function fetchRecommendations(seed, intent, limit) {
    var key = seed + ':' + intent;

    if (recsCache[key]) {
      return Promise.resolve(recsCache[key]);
    }

    return fetch(buildUrl(seed, intent, limit), { credentials: 'same-origin' })
      .then(function (response) {
        return response.ok ? response.text() : '';
      })
      .then(function (html) {
        var holder = document.createElement('div');
        holder.innerHTML = html;

        var list = holder.querySelector('.cart-drawer__recs-list');
        var result = {
          html: list ? list.innerHTML : '',
          count: list ? parseInt(list.getAttribute('data-recs-count'), 10) || 0 : 0
        };

        recsCache[key] = result;
        return result;
      })
      .catch(function () {
        return { html: '', count: 0 };
      });
  }

  function loadRecommendations() {
    var container = drawer.querySelector('[data-cart-drawer-recs][data-source="automatic"]');

    if (!container || container.dataset.loaded === 'true') {
      return;
    }

    var seed = container.getAttribute('data-seed-product-id');
    var intent = container.getAttribute('data-intent') || 'complementary';
    var limit = container.getAttribute('data-limit');

    if (!seed) {
      return;
    }

    container.dataset.loaded = 'true';

    fetchRecommendations(seed, intent, limit)
      .then(function (result) {
        // Step 1 won: the Search & Discovery pairings produced a usable strip.
        //
        // Test the RENDERED markup rather than the raw API count: the section
        // filters out cart items and, when "one-click products only" is on,
        // anything with variants — so the API can return products while the
        // strip still comes back empty.
        if (result.html.trim()) {
          return result;
        }

        // Step 2: the customizer collection, when the merchant has set one.
        // It overrides the recommendation engine, not the pairings, so it is
        // only consulted once step 1 has come back empty. Already rendered
        // server side into a <template>, so this costs no request.
        var fallback = container.querySelector('[data-cart-drawer-recs-collection]');

        if (fallback && fallback.innerHTML.trim()) {
          return { html: fallback.innerHTML, count: 0 };
        }

        // Step 3: Shopify's recommendation engine.
        if (intent === 'complementary') {
          return fetchRecommendations(seed, 'related', limit);
        }

        return result;
      })
      .then(function (result) {
        var list = container.querySelector('.cart-drawer__recs-list');

        if (!list || !result.html.trim()) {
          return;
        }

        list.innerHTML = result.html;
        container.hidden = false;
      });
  }

  // The theme fires this (without bubbling) on the cart action item after it
  // swaps the drawer's contents, so every refresh gets fresh recommendations.
  if (cartActionItem) {
    cartActionItem.addEventListener('cart:rerendered', function () {
      if (isOpen()) {
        loadRecommendations();
      }
    });
  }

  /* -------------------------------------------------------------------------
   * One-click add from the recommendation strip
   * ---------------------------------------------------------------------- */

  document.addEventListener('click', function (event) {
    var button = event.target.closest ? event.target.closest('[data-cart-drawer-quick-add]') : null;

    if (!button || button.disabled) {
      return;
    }

    event.preventDefault();

    var variantId = button.getAttribute('data-variant-id');
    var originalLabel = button.innerHTML;

    button.disabled = true;
    drawer.classList.add('is-busy');

    fetch(window.routes.cartAddUrl + '.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    })
      .then(function (response) {
        if (!response.ok) {
          throw new Error('Add to cart failed');
        }

        return response.json();
      })
      .then(function () {
        button.innerHTML = (window.languages && window.languages.productAddedShort) || 'Added';

        // Hand back to the theme: it re-renders the drawer and updates the
        // header count from the server response. scrollToTop is suppressed so
        // the page behind the drawer stays put.
        root.dispatchEvent(
          new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: { scrollToTop: false }
          })
        );
      })
      .catch(function () {
        button.innerHTML = originalLabel;
        button.disabled = false;
      })
      .then(function () {
        drawer.classList.remove('is-busy');
      });
  });
})();
